<template>
<!-- eslint-disable max-len -->
<edit-page
  name="Major Decision"
  model-name="teamDecision"
  param="teamDecisionId"
  page-prefix="/blade/major-decisions"
  :create="createTeamDecision"
  :fetch="getTeamDecision"
  :fetch-types="getTeamDecisionTypes"
  :update="updateTeamDecision"
  :remove="deleteTeamDecision"
  :enum-names="['individualDecisions', 'metrics', 'roles', 'teamDecisions', 'decisionTypes', 'choiceDisplayTypes', 'formEntryTypes', 'characters', 'variants', 'narrativeTypes', 'speakers']"
  :stub="teamDecision"
  :validator="validator"
  :utils="utils">
<template slot-scope="{ model: teamDecision, enums: { individualDecisions, metrics, roles, teamDecisions, decisionTypes, choiceDisplayTypes, formEntryTypes, characters, variants, narrativeTypes, speakers }, utils: { unused }, validator }">
  <text-input label v-model.trim="teamDecision.name" :validator="validator.name" required autofocus>Major Decision Name</text-input>

  <text-input label v-model.trim="teamDecision.title" :validator="validator.title" required>Major Decision Title</text-input>

  <text-input label v-model.trim="teamDecision.description" textarea>Major Decision Description (Internal)</text-input>

  <checkbox-input v-model="teamDecision.feedback">Feedback</checkbox-input>

  <checkbox-input v-model="teamDecision.waitIfNoDecision">
    Wait For Everyone If No Decision Specified For Role
  </checkbox-input>

  <checkbox-input v-model="teamDecision.isCheckpoint">Is Reporting Checkpoint</checkbox-input>

  <text-input label v-if="teamDecision.isCheckpoint" v-model.trim="teamDecision.checkpointLabel" :validator="validator.checkpointLabel" required>Reporting Label (i.e. Stage 1)</text-input>

  <checkbox-input v-model="teamDecision.end">Ends Section</checkbox-input>

  <branch-point-rules-input
    v-if="!teamDecision.end"
    :branchPointRules="teamDecision.branchPointRules"
    :validator="validator.branchPointRules"
    :decisionOptions="teamDecisions"
    :metricOptions="metrics"
    :narrative-types="narrativeTypes"
    :speakers="speakers"
    :characters="characters"
    :variants="variants"
  ></branch-point-rules-input>

  <select-input
    v-if="!teamDecision.end"
    label
    v-model="teamDecision.next"
    :options="teamDecisions"
    use="_id"
    display="name"
    :to="(id) => { return `/blade/major-decisions/${id}/edit`; }"
  >Next Major Decision{{ teamDecision.branchPointRules.length > 0 ? ' (if all Branch Point Rules are false)' : '' }}</select-input>

  <array-input
    :items="teamDecision.preMetricAdjustments"
    :disabled="validator.preMetricAdjustments.$invalid"
    :hide-button="teamDecision.preMetricAdjustments.length >= metrics.filter(m => m.type === 'team').length"
  >
    <h5 slot="title">Pre-Decision Metric Adjustments</h5>

    <div class="form-row" slot="item" slot-scope="{ item: preMetricAdjustment, index: i }">
      <select-input
        class="col-sm-6"
        v-model="preMetricAdjustment.metric"
        :validator="validator.preMetricAdjustments.$each[i].metric"
        :options="unused(metrics.filter(m => m.type === 'team')).by('_id').in(teamDecision.preMetricAdjustments).by('metric').at(i)"
        use="_id"
        :display="m => { return `${m.name} (${m.type})`; }"
        required
        autofocus
        :to="(id) => { return `/blade/metrics/${id}/edit`; }"
      >
        Metric
      </select-input>

      <number-input
        class="col-sm-3"
        v-model="preMetricAdjustment.adjustment"
        :validator="validator.preMetricAdjustments.$each[i].adjustment"
        required
        :remove="() => { teamDecision.preMetricAdjustments.splice(i, 1); }"
      >Adjustment</number-input>

      <checkbox-input
        class="col-sm-2"
        v-model="preMetricAdjustment.setToValue"
      >
        Set
      </checkbox-input>
    </div>
    <span slot="button">Add Pre-Decision Metric Adjustment</span>
  </array-input>

  <collapsible-section
    name="Major Decision Display Settings (applies to all Role Decisions)"
    id="GDS"
    bold
    :buttonStyle="{
      'font-size': '140%'
    }"
  >
    <checkbox-input v-model="teamDecision.forceEnableBack">
      Force Enable Backing Up To Narrative (overrides Gamulation)
    </checkbox-input>

    <checkbox-input v-model="teamDecision.preventEnableBack">
      Prevent Enable Backing Up To Narrative (overrides Gamulation)
    </checkbox-input>

    <text-input label v-model.trim="teamDecision.submitButtonText">Submit Button Text (default: Approve)</text-input>

    <asset-select-input
      label
      v-model="teamDecision.submitButtonSound"
    >
      Submit Button Sound
    </asset-select-input>

    <text-input label v-model.trim="teamDecision.backButtonText">Back Button Text (default: Back)</text-input>

    <checkbox-input v-model="teamDecision.hideTeam">Hide Team</checkbox-input>

    <checkbox-input v-model="teamDecision.hideCashInDashboard">Hide Cash In Dashboard</checkbox-input>

    <checkbox-input v-model="teamDecision.hideCashInResults">Hide Cash In Results</checkbox-input>

    <checkbox-input v-model="teamDecision.disableReasoningForm">Disable Reasoning Form</checkbox-input>

    <checkbox-input v-model="teamDecision.preventReasoningInputOverride">Prevent Reasoning Input Override (from Class)</checkbox-input>

    <number-input
      label
      min-value="0"
      max-value="250"
      v-model="teamDecision.reasoningMinimumLength"
      :validator="validator.reasoningMinimumLength"
    >
      Reasoning Minimum Length in Characters
    </number-input>

    <checkbox-input v-model="teamDecision.reasoningMinimumLengthOverride">Reasoning Minimum Length Override (use this over Gamulation/Class/Workgroup settings)</checkbox-input>

    <checkbox-input v-model="teamDecision.noWaitForIFrames">Don't Force Wait For IFrames To Load</checkbox-input>

    <checkbox-input v-model="teamDecision.skipResults">Skip Results</checkbox-input>

    <checkbox-input v-model="teamDecision.displayOneLineChoices">Display One Line Choices</checkbox-input>

    <checkbox-input v-model="teamDecision.dynamicHeightChoices">Dynamic Height Choices (note: disables wrong choice bubble position offset)</checkbox-input>

    <number-input
      v-if="!(teamDecision.dynamicHeightChoices)"
      label
      v-model.trim="teamDecision.choiceHeightOverride"
    >
      Choice Height Override (px)
    </number-input>

    <number-input
      label
      v-model.trim="teamDecision.choiceWidthOverride"
    >
      Choice Button Width Override (px) (only supported by choice-type decisions with list display type)
    </number-input>

    <text-input label v-model.trim="teamDecision.topBarText">Dashboard Top Bar Text (default: Section title)</text-input>

    <checkbox-input v-model="teamDecision.hideDashInNarratives">Hide Dashboard bar during Intro Narratives</checkbox-input>

    <checkbox-input v-model="teamDecision.hideDashInChoiceNarratives">Hide Dashboard bar during Choice Narratives</checkbox-input>

    <checkbox-input v-model="teamDecision.hideDashInOutroNarratives">Hide Dashboard bar during Outro Narratives</checkbox-input>

    <checkbox-input v-model="teamDecision.hideDashInDecision">Hide Dashboard bar during Decision</checkbox-input>

    <img
      v-if="teamDecision.backgroundOverride"
      class="form-image-background"
      :src="resolveAssetPath(teamDecision.backgroundOverride, 'backgrounds')"
    />
    <asset-select-input
      v-model="teamDecision.backgroundOverride"
      legacy-manifest-path="backgrounds"
      style="margin: 0 0 15px;"
    >
      Background Override
    </asset-select-input>

    <h6>Player Character</h6>
    <div class="form-row">
      <select-input
        v-model="teamDecision.playerVariant"
        :options="variants"
      >
        Player Variant
      </select-input>

      <collapsible-section
        name="Player Offset Settings"
        id="GDS-POS"
        bold
        :buttonStyle="{
        }"
        :style="{
          margin: '0 0 0 10px',
        }"
        :collapseStyle="{
        }"
      >
        <div style="display: flex; flex-direction: row;">
          <text-input
            label
            v-model.trim="teamDecision.playerXOffset"
            placeholder="X Offset (e.g. 50px)"
            no-count
          >
            X Offset (use CSS units)
          </text-input>
          <text-input
            label
            v-model.trim="teamDecision.playerYOffset"
            placeholder="Y Offset (e.g. 50px)"
            no-count
          >
            Y Offset (use CSS units)
          </text-input>
          <number-input
            label
            v-model.trim="teamDecision.playerOrderOverride"
          >
            Z Order Override
          </number-input>
        </div>
      </collapsible-section>
    </div>

    <div class="form-row">
      <select-input
        v-model="teamDecision.extraPlayerVariant"
        :options="variants"
        label
      >
        Player Extra Variant
      </select-input>
    </div>

    <checkbox-input v-model="teamDecision.noAnimation">
      Disable Character Animations
    </checkbox-input>
    <checkbox-input v-model="teamDecision.noAnimationEnter">
      Disable Character Animations Entering
    </checkbox-input>
    <checkbox-input v-model="teamDecision.noAnimationLeave">
      Disable Character Animations Leaving
    </checkbox-input>

    <collapsible-section
      name="Decision Content Position Offsets"
      id="MDDS-DCPO"
      bold
      :buttonStyle="{
        'font-size': '120%'
      }"
    >
      <h6>Decision Bubble Position Offset (optional)</h6>
      <div style="display: flex; flex-direction: row;">
        <text-input
          label
          v-model.trim="teamDecision.bubbleXOffset"
          placeholder="X Offset"
          no-count
        >
          X Offset (use CSS units) (e.g. 50px)
        </text-input>
        <text-input
          label
          v-model.trim="teamDecision.bubbleYOffset"
          placeholder="Y Offset"
          no-count
        >
          Y Offset (use CSS units) (e.g. 50px)
        </text-input>
      </div>

      <h6>Choice Position Offset (optional)</h6>
      <div style="display: flex; flex-direction: row;">
        <text-input
          label
          v-model.trim="teamDecision.choiceXOffset"
          placeholder="X Offset"
          no-count
        >
          X Offset (use CSS units) (e.g. 50px)
        </text-input>
        <text-input
          label
          v-model.trim="teamDecision.choiceYOffset"
          placeholder="Y Offset"
          no-count
        >
          Y Offset (use CSS units) (e.g. 50px)
        </text-input>
      </div>

      <h6>Reason Position Offset (optional)</h6>
      <div style="display: flex; flex-direction: row;">
        <text-input
          label
          v-model.trim="teamDecision.reasonXOffset"
          placeholder="X Offset"
          no-count
        >
          X Offset (use CSS units) (e.g. 50px)
        </text-input>
        <text-input
          label
          v-model.trim="teamDecision.reasonYOffset"
          placeholder="Y Offset"
          no-count
        >
          Y Offset (use CSS units) (e.g. 50px)
        </text-input>
      </div>
    </collapsible-section>

    <checkbox-input v-model="teamDecision.bubbleDecision">
      Bubble-style Decision Display
    </checkbox-input>
    <checkbox-input v-model="teamDecision.bubbleLeft">
      Bubble-style Decision Left Speaker
    </checkbox-input>
    <checkbox-input v-model="teamDecision.bubbleThought">
      Bubble-style Decision Display As Thought
    </checkbox-input>

    <template v-if="teamDecision.bubbleDecision">
    <collapsible-section
      name="Other Characters"
      id="MDDS-OC"
      bold
      :buttonStyle="{
        'font-size': '120%'
      }"
    >
      <h6>Right Character</h6>
      <div class="form-row">
        <checkbox-input
          v-model="teamDecision.rightNarrator"
          class="col-sm-2"
          @input="() => { if (teamDecision.rightNarrator) { teamDecision.rightCharacter = null; } }"
        >
          Right Narrator
        </checkbox-input>
        <character-select-input
          v-if="!(teamDecision.rightNarrator)"
          class="col-sm-3"
          select-only-id
          v-model="teamDecision.rightCharacter"
          :default-filters="{narrator: true}"
          use="_id"
          display="name"
          :to="(id) => { return `/blade/characters/${id}/edit`; }"
        >
          Right Character
        </character-select-input>
        <div class="col-sm-7 row ml-1">
          <select-input
            class="col-sm-6"
            v-model="teamDecision.rightVariant"
            :options="variantOptions(characters, variants, teamDecision.rightCharacter)"
          >
            Right Variant
          </select-input>

          <b-button
            class="col-sm-5"
            v-b-toggle.collapse-roff
            size="sm"
            style="height: 20px;"
          >
            <strong>Right Character Offset Settings</strong>
          </b-button>
          <div class="row">
            <select-input
              class="col-sm-6"
              v-model="teamDecision.rightExtraVariant"
              :options="variantOptions(characters, variants, teamDecision.rightCharacter)"
              label
            >
              Right Extra Variant
            </select-input>
          </div>
        </div>
        <b-collapse
          id="collapse-roff"
          class="form-row form-drawer col-sm-12"
        >
          <text-input
            class="col-sm-3"
            v-model.trim="teamDecision.rightXOffset"
            placeholder="X Offset (e.g. 50px)"
            label
            no-count
          >
            X Offset (use CSS units)
          </text-input>
          <text-input
            class="col-sm-3"
            v-model.trim="teamDecision.rightYOffset"
            placeholder="Y Offset (e.g. 50px)"
            label
            no-count
          >
            Y Offset (use CSS units)
          </text-input>
          <number-input
            class="col-sm-3"
            v-model.trim="teamDecision.rightOrderOverride"
            label
          >
            Z Order Override
          </number-input>
        </b-collapse>
      </div>

      <h6>Extra Left Character</h6>
      <div class="form-row">
        <checkbox-input
          v-model="teamDecision.extraLeftNarrator"
          class="col-sm-2"
          @input="() => { if (teamDecision.extraLeftNarrator) { teamDecision.extraLeftCharacter = null; } }"
        >
          Extra Left Narrator
        </checkbox-input>
        <character-select-input
          v-if="!(teamDecision.extraLeftNarrator)"
          class="col-sm-3"
          select-only-id
          v-model="teamDecision.extraLeftCharacter"
          :default-filters="{narrator: true}"
          use="_id"
          display="name"
          :to="(id) => { return `/blade/characters/${id}/edit`; }"
        >
          Extra Left Character
        </character-select-input>
        <div class="col-sm-7 row ml-1">
          <select-input
            class="col-sm-6"
            v-model="teamDecision.extraLeftVariant"
            :options="variantOptions(characters, variants, teamDecision.extraLeftCharacter)"
          >
            Extra Left Variant
          </select-input>
          <b-button
            class="col-sm-5"
            v-b-toggle.collapse-leoff
            size="sm"
            style="height: 20px;"
          >
            <strong>Extra Left Character Offset Settings</strong>
          </b-button>
          <div class="row">
            <select-input
              class="col-sm-6"
              v-model="teamDecision.extraLeftExtraVariant"
              :options="variantOptions(characters, variants, teamDecision.extraLeftCharacter)"
              label
            >
              Extra Left Extra Variant
            </select-input>
          </div>
        </div>
        <b-collapse
          id="collapse-leoff"
          class="form-row form-drawer col-sm-12"
        >
          <text-input
              class="col-sm-3"
              v-model.trim="teamDecision.extraLeftXOffset"
              placeholder="X Offset (e.g. 50px)"
              no-count
              label
            >
              X Offset (use CSS units)
            </text-input>
            <text-input
              class="col-sm-3"
              v-model.trim="teamDecision.extraLeftYOffset"
              placeholder="Y Offset (e.g. 50px)"
              no-count
              label
            >
              Y Offset (use CSS units)
            </text-input>
            <number-input
              v-model.trim="teamDecision.extraLeftOrderOverride"
              class="col-sm-3"
              label
            >
              Z Order Override
            </number-input>
        </b-collapse>
      </div>

      <h6>Extra Right Character</h6>
      <div class="form-row">
        <checkbox-input
          v-model="teamDecision.extraRightNarrator"
          class="col-sm-2"
          @input="() => { if (teamDecision.extraRightNarrator) { teamDecision.extraRightCharacter = null; } }"
        >
          Extra Right Narrator
        </checkbox-input>
        <character-select-input
          v-if="!(teamDecision.extraRightNarrator)"
          select-only-id
          class="col-sm-3"
          v-model="teamDecision.extraRightCharacter"
          :default-filters="{narrator: true}"
          use="_id"
          display="name"
          :to="(id) => { return `/blade/characters/${id}/edit`; }"
        >
          Extra Right Character
        </character-select-input>
        <div class="row col-sm-7 ml-1">
          <select-input
            class="col-sm-6"
            v-model="teamDecision.extraRightVariant"
            :options="variantOptions(characters, variants, teamDecision.extraRightCharacter)"
          >
            Extra Right Variant
          </select-input>

          <b-button
            class="col-sm-5"
            v-b-toggle.collapse-reoff
            size="sm"
            style="height: 20px;"
          >
            <strong>Extra Right Character Offset Settings</strong>
          </b-button>

          <div class="row">
            <select-input
              class="col-sm-6"
              v-model="teamDecision.extraRightExtraVariant"
              :options="variantOptions(characters, variants, teamDecision.extraRightCharacter)"
              label
            >
              Extra Right Extra Variant
            </select-input>
          </div>
        </div>

        <b-collapse
          id="collapse-reoff"
          class="form-row form-drawer col-sm-12"
        >
          <text-input
            class="col-sm-3"
            v-model.trim="teamDecision.extraRightXOffset"
            placeholder="X Offset (e.g. 50px)"
            no-count
            label
          >
            X Offset (use CSS units)
          </text-input>
          <text-input
            class="col-sm-3"
            v-model.trim="teamDecision.extraRightYOffset"
            placeholder="Y Offset (e.g. 50px)"
            no-count
            label
          >
            Y Offset (use CSS units)
          </text-input>
          <number-input
            class="col-sm-3"
            v-model.trim="teamDecision.extraRightOrderOverride"
            label
          >
            Z Order Override
          </number-input>
        </b-collapse>
      </div>
    </collapsible-section>
    </template>

    <collapsible-section
      name="Hidden Team Metrics"
      id="MDDS-HTM"
      bold
      :buttonStyle="{
        'font-size': '120%'
      }"
    >
      <array-input
        :items="teamDecision.hiddenTeamMetrics"
        :hide-button="teamDecision.hiddenTeamMetrics.length >= metrics.filter(m => m.type === 'team').length"
      >
        <h5 slot="title"></h5>

        <div class="form-row" slot="item" slot-scope="{ item: hiddenMetric, index: i }">
          <select-input
            class="col-sm-6"
            v-model="hiddenMetric._id"
            :validator="validator.hiddenTeamMetrics.$each[i]._id"
            :options="unused(metrics.filter(m => m.type === 'team')).by('_id').in(teamDecision.hiddenTeamMetrics).by('_id').at(i)"
            use="_id"
            :display="m => { return `${m.name} (${m.type})`; }"
            required
            autofocus
            :to="(id) => { return `/blade/metrics/${id}/edit`; }"
            :remove="() => { teamDecision.hiddenTeamMetrics.splice(i, 1); }"
          >
            Metric
          </select-input>
          <checkbox-input class="col-sm-2" v-model="hiddenMetric.showInDashboard">Show in Dashboard</checkbox-input>
          <checkbox-input class="col-sm-2" v-model="hiddenMetric.showInResults">Show in Results</checkbox-input>
        </div>
        <span slot="button">Hide Team Metric</span>
      </array-input>
    </collapsible-section>

    <collapsible-section
      name="Tooltips"
      id="MDDS-Tooltips"
      bold
      :buttonStyle="{
        'font-size': '120%'
      }"
    >
      <tooltip-input
        :tooltips="teamDecision.tooltips"
        :target-types="tooltipTargetTypes"
        no-title
      >Tooltips</tooltip-input>
    </collapsible-section>
  </collapsible-section>

  <collapsible-section
    name="Major Decision Intro Narrative"
    id="MDIN"
    bold
    :buttonStyle="{
      'font-size': '140%'
    }"
  >
    <narrative-input
      :narratives="teamDecision.introNarratives"
      :validator="validator.introNarratives"
      :narrative-types="narrativeTypes"
      :speakers="speakers"
      :characters="characters"
      :variants="variants"
      required
      no-title
    >Major Decision Intro Narrative</narrative-input>
  </collapsible-section>

  <checkbox-input v-model="teamDecision.useLoopbackIntro">
    Use Different Intro Narrative On Loopback
  </checkbox-input>

  <collapsible-section
    v-if="teamDecision.useLoopbackIntro"
    name="Major Decision Loopback Intro Narrative"
    id="MDRIN"
    bold
    :buttonStyle="{
      'font-size': '140%'
    }"
  >
    <narrative-input
      :narratives="teamDecision.loopbackIntroNarratives"
      :validator="validator.loopbackIntroNarratives"
      :narrative-types="narrativeTypes"
      :speakers="speakers"
      :characters="characters"
      :variants="variants"
      required
      no-title
    >Major Decision Loopback Intro Narrative</narrative-input>
  </collapsible-section>

  <template
    v-if="
      teamDecision.decisions.length > 0
      && ['matching'].includes(teamDecision.decisions[0].type)
      && teamDecision.decisions[0].choiceDisplayType === 'freeDrag'
    "
  >
    <button
      class="btn"
      style="
        z-index: 10000;
        position: fixed;
        top: 44px;
        left: 8px;
        height: 30px;
      "
      @click.prevent="
        () => { previewDecision = (previewDecision !== teamDecision.decisions[0]) ? teamDecision.decisions[0] : null; }
      "
    >Toggle Previewer / Editor</button>
    <decision-free-drag
      v-if="previewDecision != null"
      class="decision-preview"
      style="
        z-index: 9999;
        position: fixed;
        top: 44px;
        left: unset;
        right: 0;
        height: 900px;
        width: 1600px;
        background-color: rgba(0, 0, 0, 0.5);
        background-repeat: no-repeat;
        background-size: cover;
        transform-origin: top right;
      "
      :style="{
        'background-image': `url(${resolveAssetPath(teamDecision.backgroundOverride, 'backgrounds')})`,
        transform: `scale(${previewScale})`,
      }"
      :decision="previewDecision"
      editing
    ></decision-free-drag>
  </template>

  <array-input
    :items="teamDecision.decisions"
    :stub="{
      choices: [],
      categories: [],
      formEntries: [],
      narratorIntros: [],
      narratorOutros: [],
      introNarratives: [],
      outroNarratives: [],
    }"
    :disabled="validator.decisions.$invalid && teamDecision.decisions.length > 0"
    :validator="validator.decisions"
    required
    border
  >
    <h4 slot="title">Role Decisions</h4>

    <template slot="item" slot-scope="{ item: decision, index: i }">
      <text-input
        label
        v-model.trim="decision.title"
        :validator="validator.decisions.$each[i].title"
        required
        autofocus
        :remove="() => { teamDecision.decisions.splice(i, 1); }"
      >Decision Title</text-input>

      <div class="form-row">
        <select-input
          label
          v-model="decision.type"
          :options="decisionTypes"
          :validator="validator.decisions.$each[i].type"
          required
          @input="onTypeChange(decision)"
          class="col-6"
        >Decision Type</select-input>

        <select-input
          label
          v-if="decision.type && decision.type !== 'vote'"
          v-model="decision.role"
          :validator="validator.decisions.$each[i].role"
          :options="unused(roles).by('_id').in(teamDecision.decisions).by('role').at(i)"
          use="_id"
          display="name"
          required
          :to="(id) => { return `/blade/roles/${id}/edit`; }"
          class="col-6"
        >Role</select-input>
      </div>

      <collapsible-section
        :name="`Role Decision Settings for: ${removeTags(decision.title) || '[missing title]'}`"
        :id="`subd-set${i}`"
        ellipsis
        bold
        indent
        :buttonStyle="{
          'font-size': '110%'
        }"
      >
        <text-input
          label
          v-model.trim="decision.referenceId"
        >Reference ID (optional, used by text replacements, needs to be unique for a given playthrough)</text-input>

        <checkbox-input v-model="decision.hideTitle">
          No Title In Decision Display (still used for Activity Panel)
        </checkbox-input>

        <text-input
          v-if="!(decision.hideTitle)"
          label
          v-model.trim="decision.displayTitle"
        >Display Title (falls back to Decision Title)</text-input>

        <voice-line-input
          :text="decision.displayTitle || decision.title"
          :data-object="decision"
          field-name="titleVoiceLine"
          :character="
            (teamDecision.bubbleDecision)
              ? ((teamDecision.bubbleLeft)
                ? teamDecision.leftCharacter
                : teamDecision.rightCharacter)
              : ''
          "
        ></voice-line-input>

        <text-input
          label
          v-model.trim="decision.reportTitle"
        >Report Title (falls back to Major Decision Title)</text-input>

        <text-input
          label
          v-model.trim="decision.activityWindowTitle"
        >Activity Window Title (falls back to Report Title)</text-input>

        <number-input v-if="teamDecision.decisions.length > 1" label v-model="decision.order" style="width: 30%;">Decision Order (for multiple decisions)</number-input>

        <text-input label v-model.trim="decision.description" textarea>Decision Description</text-input>

        <checkbox-input v-model="decision.isDescriptionMarkdown">
          Render Description As Markdown
        </checkbox-input>

        <voice-line-input
          :text="decision.description"
          :data-object="decision"
          field-name="descriptionVoiceLine"
          :character="
            (teamDecision.bubbleDecision)
              ? ((teamDecision.bubbleLeft)
                ? teamDecision.leftCharacter
                : teamDecision.rightCharacter)
              : ''
          "
        ></voice-line-input>

        <number-input v-if="['choice', 'survey'].includes(decision.type)" v-model="decision.timeLimit" label>
          Time Limit In Seconds (if time runs out the first Default Choice is selected (or the last choice if unspecified))
        </number-input>
        <checkbox-input
          v-if="['choice', 'survey'].includes(decision.type)"
          v-model="decision.carryOverLastTimeLimit"
        >If previous decision had a time limit, use remaining time from the previous limit instead of the above
        </checkbox-input>
        <text-input
          v-if="['choice', 'survey'].includes(decision.type) && (decision.timeLimit || decision.carryOverLastTimeLimit)"
          label
          v-model.trim="decision.timerEndMessage"
          no-count
        >Message When Out Of Time (optional, but forced if a chain of decisions are out of time, default: Ran Out Of Time)
        </text-input>
        <number-input
          v-if="['choice', 'survey'].includes(decision.type) && (decision.timeLimit || decision.carryOverLastTimeLimit)"
          v-model="decision.timerEndMessageDuration"
          label
        >Message Duration (milliseconds) (optional, but forced if a chain of decisions are out of time, default: 2000)
        </number-input>
        <div
          v-if="['choice', 'survey'].includes(decision.type) && (decision.timeLimit || decision.carryOverLastTimeLimit)"
          style="font-size: 85%; margin-bottom: 15px;"
        >
          <strong>Note:</strong> only the last of a chain of decisions will show its Message for the full Duration; so you'll probably want to make sure the Message is consistent for the whole chain of decisions but you only need to set the last Duration of the chain if you want different behavior from the default. You could also add a count like (2 of 5) so the player can see how many decisions were skipped.
        </div>
        <div
          v-if="['choice', 'survey'].includes(decision.type) && (decision.timeLimit || decision.carryOverLastTimeLimit)"
          class="form-row"
        >
          <text-input
            label
            class="col-sm-6"
            v-model.trim="decision.timerXOffset"
            no-count
          >Timer X Offset (use CSS units) (e.g. 10px)</text-input>
          <text-input
            label
            class="col-sm-6"
            v-model.trim="decision.timerYOffset"
            no-count
          >Timer Y Offset (use CSS units) (e.g. 10px)</text-input>
        </div>

        <checkbox-input v-model="decision.forceConfirmation">Prevent Automatically Skipping Choice Confirmation</checkbox-input>

        <checkbox-input v-model="decision.forceSkipConfirmation">Force Automatically Skipping Choice Confirmation</checkbox-input>

        <template v-if="['roleAllocation', 'projectAllocation'].includes(decision.type)">
          <text-input label v-model.trim="decision.scoringObjective" required>Decision Scoring Objective</text-input>
          <text-input label v-model.trim="decision.keyPerformanceMeasure" required>Decision Key Performance Measure</text-input>
          <checkbox-input v-model="decision.sliderDisplayIntervals">Use Slider Interval</checkbox-input>
          <number-input v-if="decision.sliderDisplayIntervals" label v-model="decision.sliderInterval" non-zero>Slider Interval</number-input>
        </template>

        <text-input
          v-if="!['form'].includes(decision.type)"
          label
          v-model.trim="decision.prompt"
        >Prompt (optional)</text-input>

        <voice-line-input
          :text="decision.prompt"
          :data-object="decision"
          field-name="promptVoiceLine"
          :character="
            (teamDecision.bubbleDecision)
              ? ((teamDecision.bubbleLeft)
                ? teamDecision.leftCharacter
                : teamDecision.rightCharacter)
              : ''
          "
        ></voice-line-input>

        <text-input
          v-if="!['form'].includes(decision.type)"
          label
          v-model.trim="decision.loopbackPrompt"
        >Loopback Prompt (optional)</text-input>

        <voice-line-input
          :text="decision.loopbackPrompt"
          :data-object="decision"
          field-name="loopbackPromptVoiceLine"
          :character="
            (teamDecision.bubbleDecision)
              ? ((teamDecision.bubbleLeft)
                ? teamDecision.leftCharacter
                : teamDecision.rightCharacter)
              : ''
          "
        ></voice-line-input>

        <template v-if="!['form'].includes(decision.type)">
          <img v-if="decision.image" class="form-image-background" :src="resolveAssetPath(decision.image, 'images')">
          <asset-select-input @input="onDecisionImageChange(decision)" label v-model="decision.image" legacy-manifest-path="images">Decision Image</asset-select-input>
        </template>

        <template v-if="!['roleAllocation', 'projectAllocation', 'freeResponse', 'form'].includes(decision.type)">
          <video style="max-height: 330px;" v-if="decision.video" :src="resolveAssetPath(decision.video, 'videos')" class="video" controls playsinline/>
          <asset-select-input @input="onDecisionVideoChange(decision)" label v-model="decision.video" legacy-manifest-path="videos">Decision Video</asset-select-input>
        </template>

        <template v-if="!(teamDecision.bubbleDecision)">
          <text-input
            label
            v-model.trim="decision.decisionContainerMaxHeightOverride"
            no-count
          >Decision Container Max Height Override (use CSS units) (e.g. 780px) (non-bubble)</text-input>
          <text-input
            label
            v-model.trim="decision.decisionContainerWidthOverride"
            no-count
          >Decision Container Width Override (use CSS units) (e.g. 1225px) (non-bubble)</text-input>
        </template>

        <text-input
          label
          v-model.trim="decision.questionContentHeight"
          no-count
        >Question Content Height Override (use CSS units) (e.g. 330px)</text-input>

        <text-input
          v-if="teamDecision.bubbleDecision"
          label
          v-model.trim="decision.choiceBubbleMinHeightOverride"
          no-count
        >Choice Bubble Min Height Override (use CSS units) (e.g. 350px)</text-input>

        <text-input
          v-if="teamDecision.bubbleDecision"
          label
          v-model.trim="decision.choiceBubbleMaxHeightOverride"
          no-count
        >Choice Bubble Max Height Override (use CSS units) (e.g. 500px)</text-input>

        <number-input
          v-if="teamDecision.bubbleDecision"
          label
          v-model="decision.choiceCountForOverflowOverride"
          placeholder="(used for removed choice position offset; undefined or 0 defaults to 5)"
        >Max Choices Before Causing The Container To Scroll</number-input>

        <text-input
          v-if="teamDecision.bubbleDecision"
          label
          v-model.trim="decision.reasonBubbleMaxHeightOverride"
          no-count
        >Reason Bubble Height Override (use CSS units) (e.g. 700px)</text-input>

        <number-input
          v-if="['scale', 'matrix'].includes(decision.type)"
          label
          v-model="decision.choiceColumnWidthPercent"
        >Choice Column Width Percent (optional; value in range (0, 100]; category columns evenly use the rest)</number-input>

        <select-input
          v-if="['matching'].includes(decision.type)"
          label
          v-model="decision.unselectedCategory"
          :options="decision.categories || []"
          use="_id"
          :display="e => { return `${e.name}`; }"
        >Unselected Category (choices in this category never count as incorrect)</select-input>

        <select-input
          v-if="['choice', 'matching'].includes(decision.type)"
          label
          v-model="decision.choiceDisplayType"
          :options="filteredChoiceDisplayTypes(decision, choiceDisplayTypes)"
        >Choice Display Type</select-input>

        <checkbox-input
          v-if="['choice', 'matching'].includes(decision.type)"
          v-model="decision.preventChoiceDisplayTypeOverride"
        >Prevent Choice Display Type Override (from {{classTypeName}})</checkbox-input>

        <template v-if="['matching'].includes(decision.type)">
          <template v-if="decision.choiceDisplayType === 'freeDrag'">
            <collapsible-section
              name="Overlapping Item Drop Settings"
              :id="`subd-set${i}-oids`"
              :buttonStyle="{
                'font-size': '110%'
              }"
            >
              <checkbox-input
                v-model="decision.overlapItemDropDisplayWarning"
              >Overlapping Item Drop Should Display Warning</checkbox-input>
              <text-input
                v-if="decision.overlapItemDropDisplayWarning"
                label
                v-model.trim="decision.overlapItemDropWarningMessage"
                placeholder="Items cannot overlap."
              >Overlapping Item Drop Warning Message (default: "Items cannot overlap.")</text-input>
              <checkbox-input
                v-model="decision.overlapItemDropCancel"
              >Overlapping Item Drop Should Revert The Drop</checkbox-input>
              <checkbox-input
                v-model="decision.overlapItemDropAttemptCorrection"
              >Overlapping Item Drop Should Attempt To Correct Overlap (won't always work due to category bounds and other items)</checkbox-input>
            </collapsible-section>
            <!-- duplicate button to reduce possibility of toggle button being impossible to click -->
            <button
              class="btn"
              style="
                height: 30px;
                margin: 12px 0;
              "
              @click.prevent="() => { previewDecision = (previewDecision !== decision) ? decision : null; }"
            >Toggle Previewer / Editor</button>
          </template>
          <template v-else>
            <text-input
              label
              v-model.trim="decision.categoriesAreaHeight"
              no-count
            >Categories Scroll Area Height (use CSS units) (default dynamically shares space with Choices Scroll Area)</text-input>
            <text-input
              label
              v-model.trim="decision.choicesAreaHeight"
              no-count
            >Choices Scroll Area Height (use CSS units) (default dynamically shares space with Categories Scroll Area)</text-input>
            <checkbox-input v-model="decision.categoriesAsRows">Categories As Rows (instead of columns)</checkbox-input>
            <number-input label v-model="decision.categoriesWrapLimit">Categories Wrap Limit (&lt; 1 for no wrap)</number-input>
            <text-input
              label
              v-model.trim="decision.categoryMinHeight"
              no-count
            >Category Drag Container Min Height (use CSS units) (default 110px, expands to fit held choices)</text-input>
            <number-input
              label
              v-model="decision.choicesWrapLimit"
            >Choices Wrap Limit (controls choice width; defaults to same choice sizing logic as choice-type decisions)</number-input>
            <number-input
              label
              v-model="decision.categoriesChoicesWrapLimit"
              placeholder="(defaults to (Choices Wrap Limit / Categories Wrap Limit) if Categories Wrap Limit > 1, otherwise Choices Wrap Limit)"
            >Choices Wrap Limit Inside Category Drag Container (default computed from Choices Wrap Limit & Categories Wrap Limit)</number-input>
          </template>
        </template>
        <template v-if="['matching', 'scale', 'matrix'].includes(decision.type)">
          <array-input
            :items="decision.categories"
            :disabled="validator.decisions.$each[i].categories.$invalid && decision.categories.length > 0"
            :validator="validator.decisions.$each[i].categories"
            border
            generate-id
          >
            <h4 slot="title">Role Decision Categories{{decision.categories && decision.categories.length ? '' : ' ||| [AT LEAST 1 REQUIRED]'}}</h4>

            <template slot="item" slot-scope="{ item: category, index: j }">
              <text-input
                label
                v-model.trim="category.name"
                :validator="validator.decisions.$each[i].categories.$each[j].name"
                required
                autofocus
                :remove="() => { decision.categories.splice(j, 1); }"
                :up="() => { up(decision.categories, j); }"
                :down="() => { down(decision.categories, j); }"
              >Name</text-input>
              <text-input
                label
                v-model.trim="category.displayName"
              >Display Name</text-input>
              <number-input
                v-if="['scale'].includes(decision.type)"
                label
                v-model="category.value"
              >Value</number-input>

              <template v-if="['matching'].includes(decision.type) && decision.choiceDisplayType === 'freeDrag'">
                <number-input label v-model="category.xPos">X Position (pixels, 1600x900 reference size)</number-input>
                <number-input label v-model="category.yPos">Y Position (pixels, 1600x900 reference size)</number-input>
                <number-input label v-model="category.width">Width (pixels, 1600x900 reference size)</number-input>
                <number-input label v-model="category.height">Height (pixels, 1600x900 reference size)</number-input>
                <text-input
                  label
                  v-model.trim="category.color"
                  no-count
                >Highlight Color (any valid CSS color, default rgba(128, 128, 128, 1))</text-input>
              </template>

              <div style="background-color: grey; height: 2px; width: 100%;"></div>
            </template>
            <span slot="button">Add Category</span>
          </array-input>
        </template>

        <checkbox-input
          v-if="!(['form'].includes(decision.type))"
          v-model="decision.forceRandomChoiceOrder"
        >Force Random Choice Order (overrides {{classTypeName}} setting)</checkbox-input>

        <checkbox-input
          v-if="!(['form'].includes(decision.type))"
          v-model="decision.preventRandomChoiceOrder"
        >Prevent Random Choice Order (overrides {{classTypeName}} setting)</checkbox-input>

        <checkbox-input
          v-if="['choice'].includes(decision.type) && ['carousel'].includes(decision.choiceDisplayType)"
          v-model="decision.hideChoiceHeader"
        >Hide Choice Header (choice descriptions only)</checkbox-input>

        <asset-select-input
          v-if="
            ['multiselect', 'form', 'sliders', 'matching', 'scale', 'matrix'].includes(decision.type)
            || (decision.type === 'choice' && decision.choiceDisplayType === 'carousel')
          "
          label
          v-model="decision.choiceInputChangeSound"
        >
          Choice Input Change Sound
        </asset-select-input>

        <asset-select-input
          v-if="['multiselect', 'matrix'].includes(decision.type)"
          label
          v-model="decision.choiceInputOffSound"
        >
          Choice Input Off Sound (checkbox unticked sound; falls back to Choice Input Change Sound)
        </asset-select-input>

        <text-input
          v-if="['choice', 'multiselect', 'form', 'sliders', 'matching', 'scale', 'matrix'].includes(decision.type)"
          label
          v-model.trim="decision.choiceSelectButtonText"
          :placeholder="
            (['multiselect', 'matrix'].includes(decision.type))
              ? 'default: Select'
              : ((decision.type === 'choice' && decision.choiceDisplayType === 'carousel')
                ? 'default: Approve'
                : ((decision.type === 'form')
                  ? 'default: Submit'
                  : (['sliders', 'scale'].includes(decision.type)
                    ? 'default: Set'
                    : ((decision.type === 'matching')
                      ? 'default: Submit Matches'
                      : 'N/A'))))
          "
        >Choice Select Button Text (optional){{(decision.type === 'choice') ? ' (applies to carousel display type)' : ''}}</text-input>

        <asset-select-input
          v-if="['choice', 'survey', 'multiselect', 'form', 'sliders', 'matching', 'scale', 'matrix'].includes(decision.type)"
          label
          v-model="decision.choiceSelectButtonSound"
        >
          Choice Select{{!(['survey'].includes(decision.type)) ? ' Button' : ''}} Sound
        </asset-select-input>

        <number-input
          v-if="decision.type && decision.type === 'multiselect'"
          v-model="decision.multiselectMinChoices"
          label
        >Min Choices Selected</number-input>

        <number-input
          v-if="decision.type && decision.type === 'multiselect'"
          v-model="decision.multiselectMaxChoices"
          label
        >Max Choices Selected</number-input>

        <checkbox-input
          v-if="decision.type && decision.type === 'multiselect'"
          v-model="decision.multiselectChoicesLimitsDisabledOnLoopback"
        >Selected Choices Limits Disabled On Loopback</checkbox-input>

        <text-input label v-if="decision.type && decision.type === 'survey'" v-model.trim="decision.minLabel">Min Label</text-input>

        <text-input label v-if="decision.type && decision.type === 'survey'" v-model.trim="decision.maxLabel">Max Label</text-input>

        <checkbox-input
          v-if="decision.type && decision.type === 'survey'"
          v-model="decision.equalSizedChoices"
        >Force Equal-Sized Choices</checkbox-input>
      </collapsible-section>

      <collapsible-section
        :name="`Role Decision Intros for: ${removeTags(decision.title) || '[missing title]'}`"
        :id="`subd-in${i}`"
        ellipsis
        bold
        indent
        :buttonStyle="{
          'font-size': '110%'
        }"
      >
        <narrative-input
          :narratives="decision.introNarratives"
          :validator="validator.decisions.$each[i].introNarratives"
          :narrative-types="narrativeTypes"
          :speakers="speakers"
          :characters="characters"
          :variants="variants"
          required
        >
          Role Decision Intro Narrative
        </narrative-input>

        <array-input :items="decision.narratorIntros" :disabled="validator.decisions.$each[i].narratorIntros.$invalid && decision.narratorIntros.length > 0" :validator="validator.decisions.$each[i].narratorIntros" border>
          <h4 slot="title">Role Decision Narrator Intros</h4>

          <template slot="item" slot-scope="{ item: narratorIntro, index: j }">
            <text-input label v-model.trim="narratorIntro.text" :validator="validator.decisions.$each[i].narratorIntros.$each[j].text" required autofocus :remove="() => { decision.narratorIntros.splice(j, 1); }">Text {{ j + 1 }}</text-input>
          </template>
          <span slot="button">Add Narrator Intros</span>
        </array-input>
      </collapsible-section>

      <collapsible-section
        v-if="['form'].includes(decision.type)"
        :name="
          `Role Decision Form Entries for: ${
            removeTags(decision.title) || '[missing title]'
          }${
            decision.formEntries && decision.formEntries.length ? '' : ' ||| [AT LEAST 1 REQUIRED]'
          }`
        "
        :id="`subd-fe${i}`"
        ellipsis
        bold
        indent
        :buttonStyle="{
          'font-size': '110%',
        }"
      >
        <array-input
          v-if="['form'].includes(decision.type)"
          :items="decision.formEntries"
          :stub="formEntryStub"
          :disabled="validator.decisions.$each[i].formEntries.$invalid && decision.formEntries.length > 0"
          :validator="validator.decisions.$each[i].formEntries"
          :required="['form'].includes(decision.type)"
          border
        >
          <h4 slot="title">Form Entries</h4>

          <template slot="item" slot-scope="{ item: formEntry, index: j }">
            <div style="width: 100%; display: flex; flex-flow: row nowrap;">
              <text-input
                style="flex-grow: 1;"
                label
                v-model.trim="formEntry.prompt"
                :validator="validator.decisions.$each[i].formEntries.$each[j].prompt"
                required
                autofocus
                :remove="() => { remove(decision.formEntries, j); }"
              >Form Entry Prompt</text-input>

              <button
                class="btn"
                style="height: 38px; margin: 32px 0 0 10px;"
                @click.prevent="()=>{ decision.formEntries.splice(j, 0, formEntryStub); }"
                :disabled="validator.decisions.$each[i].formEntries.$invalid && decision.formEntries.length > 0"
              >Add Entry Before</button>
            </div>

            <select-input
              label
              v-model="formEntry.type"
              :options="formEntryTypes"
              :validator="validator.decisions.$each[i].formEntries.$each[j].type"
              required
              @input="onFormEntryTypeChange(formEntry)"
            >Form Entry Type</select-input>

            <text-input
              label
              v-model.trim="formEntry.referenceId"
            >Reference ID (optional, used by text replacements, needs to be unique for a given playthrough)</text-input>

            <select-input
              v-if="['employmentStartDate'].includes(formEntry.type)"
              v-model="formEntry.dateMetric"
              label
              :options="metrics"
              use="_id"
              :display="m => { return `${m.name} (${m.type})`; }"
              :to="(id) => { return `/blade/metrics/${id}/edit`; }"
            >Date Metric (saves as number representation of JS date)</select-input>

            <select-input
              v-if="['employmentStartDate'].includes(formEntry.type)"
              v-model="formEntry.timeMetric"
              label
              :options="metrics"
              use="_id"
              :display="m => { return `${m.name} (${m.type})`; }"
              :to="(id) => { return `/blade/metrics/${id}/edit`; }"
            >Time Metric (saves as number of months at the time of answering)</select-input>

            <template v-if="['employmentStartDate', 'year'].includes(formEntry.type)">
              <h6>Year Range <span style="font-size: 85%;">(if Maximum Year is unspecified, uses the current year)</span></h6>
              <div class="form-row">
                <number-input v-model="formEntry.minYear" class="col-sm-3">Minimum Year</number-input>
                <number-input v-model="formEntry.maxYear" class="col-sm-3">Maximum Year</number-input>
              </div>
            </template>

            <template v-if="['range'].includes(formEntry.type)">
              <h6>Numerical Range</h6>
              <div class="form-row">
                <number-input v-model="formEntry.startRange" class="col-sm-3">Range Start</number-input>
                <number-input v-model="formEntry.endRange" class="col-sm-3">Range End</number-input>
              </div>
            </template>

            <text-input
              v-if="['employmentStartDate'].includes(formEntry.type)"
              label
              v-model.trim="formEntry.noDateCheckboxText"
              placeholder="I haven't started role"
            >No Date Checkbox Text</text-input>

            <select-input
              v-if="['year', 'range'].includes(formEntry.type)"
              v-model="formEntry.dateMetric"
              label
              :options="metrics"
              use="_id"
              :display="m => { return `${m.name} (${m.type})`; }"
              :to="(id) => { return `/blade/metrics/${id}/edit`; }"
            >
              {{ ['year'].includes(formEntry.type) ? 'Year Metric (saves as numerical year)' : 'Metric (saves the selected number)' }}
            </select-input>

            <collapsible-section
              v-if="['selection'].includes(formEntry.type)"
              :name="
                `Form Entry Choices for: ${
                  removeTags(formEntry.prompt) || '[missing title]'
                }${
                  formEntry.choices && formEntry.choices.length ? '' : ' ||| [AT LEAST 1 REQUIRED]'
                }`
              "
              :id="`subd-fe${i}-ch${j}`"
              ellipsis
              bold
              indent
              :buttonStyle="{
                'font-size': '105%',
              }"
            >
              <array-input
                v-if="['selection'].includes(formEntry.type)"
                :items="formEntry.choices"
                :stub="{ metricAdjustments: [], }"
                :disabled="validator.decisions.$each[i].formEntries.$each[j].choices.$invalid && formEntry.choices.length > 0"
                :validator="validator.decisions.$each[i].formEntries.$each[j].choices"
                :required="['selection'].includes(formEntry.type)"
                border
              >
                <h4 slot="title">Form Entry Choices</h4>

                <template slot="item" slot-scope="{ item: choice, index: k }">
                  <text-input
                    label
                    v-model.trim="choice.title"
                    :validator="validator.decisions.$each[i].formEntries.$each[j].choices.$each[k].title"
                    required
                    autofocus
                    :remove="() => { remove(formEntry.choices, k); }"
                  >Choice Title</text-input>

                  <array-input
                    class="offset-sm-1"
                    :items="choice.metricAdjustments"
                    :disabled="validator.decisions.$each[i].formEntries.$each[j].choices.$each[k].$invalid"
                    border
                  >
                    <h5 slot="title">Metric Adjustments</h5>

                    <template slot="item" slot-scope="{ item: metricAdjustment, index: m }">
                      <div class="form-row">
                        <select-input
                          class="col-sm-6"
                          v-model="metricAdjustment.metric"
                          :validator="validator.decisions.$each[i].formEntries.$each[j].choices.$each[k].metricAdjustments.$each[m].metric"
                          :options="unused(metrics).by('_id').in(choice.metricAdjustments).by('metric').at(m)"
                          use="_id"
                          :display="met => { return `${met.name} (${met.type})`; }"
                          required
                          autofocus
                          :remove="() => { choice.metricAdjustments.splice(k, 1); }"
                          :to="(id) => { return `/blade/metrics/${id}/edit`; }"
                        >Metric</select-input>

                        <number-input class="col-sm-3" v-model="metricAdjustment.adjustment">Adjustment</number-input>

                        <checkbox-input
                          class="col-sm-2"
                          v-model="metricAdjustment.setToValue"
                        >
                          Set
                        </checkbox-input>
                      </div>
                    </template>
                    <span slot="button">Add Metric Adjustment</span>
                  </array-input>

                  <div style="background-color: grey; height: 2px; width: 100%;"></div>
                </template>
                <span slot="button">Add Choice</span>
              </array-input>
            </collapsible-section>

            <checkbox-input v-model="formEntry.displayConditionally">
              Display Conditionally
            </checkbox-input>

            <select-input
              v-if="formEntry.displayConditionally"
              v-model="formEntry.dependentEntryOffset"
              label
              :options="dependentEntryOptions(decision, j)"
              use="value"
              display="text"
              integer
            >Dependent Entry</select-input>

            <select-input
              v-if="formEntry.displayConditionally && dependentChoiceOptions(decision, j, formEntry.dependentEntryOffset).length"
              v-model="formEntry.dependentChoiceIndex"
              label
              :options="dependentChoiceOptions(decision, j, formEntry.dependentEntryOffset)"
              use="value"
              display="text"
              integer
            >Dependent Choice</select-input>

            <div style="background-color: grey; height: 2px; width: 100%;"></div>
          </template>
          <span slot="button">Add Form Entry</span>
        </array-input>
      </collapsible-section>

      <collapsible-section
        v-if="!['freeResponse', 'form'].includes(decision.type)"
        :name="`Role Decision Choices for: ${removeTags(decision.title) || '[missing title]'}${decision.choices && decision.choices.length ? '' : ' ||| [AT LEAST 1 CHOICE REQUIRED]'}`"
        :id="`subd-ch${i}`"
        ellipsis
        bold
        indent
        :buttonStyle="{
          'font-size': '110%'
        }"
      >
        <div
          v-if="decision.type && decision.type === 'survey'"
          style="display: flex; flex-flow: row nowrap; align-items: center; height: 38px; transform: scale(0.98);"
        >
          <div>Survey Choice Autogeneration Tool:&nbsp;</div>
          <number-input v-model="autogenChoiceCount" style="width: 10%; align-self: flex-start;">Choice Count</number-input>
          <button class="btn" @click.prevent="()=>{autogenerateChoices(decision);}">Autogenerate Choices</button>
        </div>

        <array-input
          v-if="!['freeResponse', 'form'].includes(decision.type)"
          :items="decision.choices"
          :stub="{
            metricAdjustments: [],
            unselectedMetricAdjustments: [],
            narratives: [],
            selectedNarratives: [],
            unselectedNarratives: [],
          }"
          :disabled="validator.decisions.$each[i].choices.$invalid && decision.choices.length > 0"
          :validator="validator.decisions.$each[i].choices"
          :required="!['freeResponse', 'form'].includes(decision.type)"
          border
        >
          <h4 slot="title">Choices</h4>

          <template slot="item" slot-scope="{ item: choice, index: j }">
            <text-input
              label
              v-if="decision.type && decision.type !== 'roleAllocation'"
              v-model.trim="choice.title"
              :validator="validator.decisions.$each[i].choices.$each[j].title"
              required
              autofocus
              :remove="decision.type && decision.type === 'roleAllocation' ? null : () => { remove(decision.choices, j); }"
              :up="decision.type && decision.type === 'roleAllocation' ? null : () => { up(decision.choices, j); }"
              :down="decision.type && decision.type === 'roleAllocation' ? null : () => { down(decision.choices, j); }"
            >Choice Title</text-input>

            <select-input
              label
              v-if="decision.type === 'roleAllocation'"
              v-model="choice.allocateToRole"
              :validator="validator.decisions.$each[i].choices.$each[j].allocateToRole"
              :options="unused(roles).by('_id').in(decision.choices).by('allocateToRole').at(j)"
              use="_id"
              display="name"
              required
              :remove="decision.type && decision.type === 'roleAllocation' ? () => { decision.choices.splice(j, 1); } : undefined"
              :up="decision.type && decision.type === 'roleAllocation' ? () => { up(decision.choices, j); } : undefined"
              :down="decision.type && decision.type === 'roleAllocation' ? () => { down(decision.choices, j); } : undefined"
              :to="(id) => { return `/blade/roles/${id}/edit`; }"
            >Allocate To Role</select-input>
            <collapsible-section
              :name="`Choice Settings for: ${
                (decision.type === 'roleAllocation')
                  ? (getRoleName(roles, choice.allocateToRole) || '[missing target role]')
                  : (removeTags(choice.title) || '[missing title]')
              }`"
              :id="`subd-ch${i}-ch${j}`"
              ellipsis
              bold
              indent
              :buttonStyle="{
                'font-size': '110%'
              }"
            >
              <text-input
                label
                v-if="decision.type && decision.type !== 'roleAllocation'"
                v-model.trim="choice.displayNameForConfirmation"
              >Choice Display Name for Confirmation / Reason Input view</text-input>

              <text-input label v-if="decision.type && !['roleAllocation'].includes(decision.type)" v-model.trim="choice.description" textarea>Choice Description</text-input>

              <voice-line-input
                :text="choice.description"
                :data-object="choice"
                field-name="descriptionVoiceLine"
                :character="
                  (teamDecision.bubbleDecision)
                    ? teamDecision.leftCharacter
                    : ''
                "
              ></voice-line-input>

              <template v-if="['matching'].includes(decision.type) && decision.choiceDisplayType === 'freeDrag'">
                <img
                  v-if="choice.image"
                  class="form-image-background"
                  :src="resolveAssetPath(choice.image, 'images')"
                >
                <asset-select-input
                  label
                  v-model="choice.image"
                  legacy-manifest-path="images"
                >Choice Image</asset-select-input>
                <number-input label v-model="choice.scale">Image Width (pixels, 1600 reference width)</number-input>
                <number-input label v-model="choice.xPos">X Position (pixels, 1600x900 reference size)</number-input>
                <number-input label v-model="choice.yPos">Y Position (pixels, 1600x900 reference size)</number-input>
                <collapsible-section
                  name="Hover/Drag Text"
                  :id="`subd-ch${i}-ch${j}-hdt`"
                  :buttonStyle="{
                    'font-size': '110%'
                  }"
                >
                  <text-input
                    label
                    v-model.trim="choice.hoverText"
                  >Choice Hover/Drag Text</text-input>
                  <text-input
                    label
                    v-model.trim="choice.hoverTextColor"
                  >Choice Hover/Drag Text Color (default: white)</text-input>
                  <text-input
                    label
                    v-model.trim="choice.hoverTextBackgroundColor"
                  >Choice Hover/Drag Text Background Color (default: black)</text-input>
                </collapsible-section>
              </template>

              <img v-if="choice.image && decision.type && ['choice', 'survey', 'projectAllocation'].includes(decision.type)" class="form-image-background" :src="resolveAssetPath(choice.image, 'images')">
              <asset-select-input label v-if="decision.type && ['choice', 'survey', 'projectAllocation'].includes(decision.type)" v-model="choice.image" legacy-manifest-path="images">Choice Image</asset-select-input>

              <select-input
                v-if="decision.type && ['choice', 'survey', 'projectAllocation'].includes(decision.type)"
                label
                v-model="choice.playerVariantImage"
                :options="variants"
              >
                Player Variant Image (falls back to Image if variant not found)
              </select-input>

              <checkbox-input v-if="choice.image && decision.type && ['choice','survey'].includes(decision.type)" v-model="choice.showTitle">
                Show Title Under Image
              </checkbox-input>

              <checkbox-input v-if="decision.type && ['choice','survey'].includes(decision.type)" v-model="choice.startsFocused">
                Choice Button Starts Focused
              </checkbox-input>

              <img v-if="choice.playerBackgroundChange && decision.type && ['choice', 'survey'].includes(decision.type)" class="form-image-background" :src="resolveAssetPath(choice.playerBackgroundChange, 'backgrounds')">
              <asset-select-input label v-if="decision.type && ['choice', 'survey'].includes(decision.type)" v-model="choice.playerBackgroundChange" legacy-manifest-path="backgrounds">Player Background Change</asset-select-input>

              <character-select-input
                v-if="decision.type && ['choice', 'survey'].includes(decision.type)"
                select-only-id
                v-model="choice.narratorChange"
                :default-filters="{narrator: true}"
                use="_id"
                display="name"
                :to="(id) => { return `/blade/characters/${id}/edit`; }"
              >
                Gamulation Narrator Change
              </character-select-input>

              <checkbox-input
                v-if="decision.type && ['choice'].includes(decision.type)"
                @input="(checked) => { if (checked && choice.next) { choice.next = null; } }"
                v-model="choice.wrong"
              >
                Wrong Choice (remove and loopback when selected)
              </checkbox-input>

              <checkbox-input
                v-if="decision.type && ['choice', 'multiselect'].includes(decision.type)"
                v-model="choice.removeIfPreviouslyChosen"
              >
                Remove If Previously Chosen (prevent from being presented again in a Playthrough once chosen)
              </checkbox-input>

              <checkbox-input
                v-if="!['roleAllocation', 'projectAllocation', 'sliders', 'matching', 'scale', 'matrix'].includes(decision.type)"
                v-model="choice.default"
              >
                Default Choice (used when decision is skipped by group or timer)
              </checkbox-input>

              <select-input v-if="!teamDecision.end && !choice.wrong && !teamDecision.next && decision.type && ['choice', 'survey'].includes(decision.type)" label v-model="choice.next" :options="teamDecisions" use="_id" display="name" :to="(id) => { return `/blade/major-decisions/${id}/edit`; }">Next Major Decision</select-input>

              <checkbox-input
                v-if="decision.type && !['roleAllocation', 'projectAllocation', 'survey', 'sliders', 'matching', 'scale', 'matrix'].includes(decision.type)"
                v-model="choice.hideCost"
              >Hide Cost</checkbox-input>

              <number-input
                label
                v-if="decision.type && !['roleAllocation', 'projectAllocation', 'survey', 'sliders', 'matching', 'scale', 'matrix'].includes(decision.type)"
                v-model="choice.displayedCost"
                :validator="validator.decisions.$each[i].choices.$each[j].displayedCost"
                non-zero
              >Cost/Displayed Cash Adjustment (if different from Cash Adjustment below)</number-input>

              <number-input
                label
                v-if="decision.type && !['roleAllocation', 'projectAllocation', 'sliders', 'matching', 'scale', 'matrix'].includes(decision.type)"
                v-model="choice.cashAdjustment"
                :validator="validator.decisions.$each[i].choices.$each[j].cashAdjustment"
                non-zero
              >Cash Adjustment</number-input>

              <template v-if="decision.type && ['sliders'].includes(decision.type)">
                <number-input label v-model="choice.sliderDefault" :validator="validator.decisions.$each[i].choices.$each[j].sliderDefault" non-zero>Default Slider Value</number-input>
                <number-input label v-model="choice.sliderMin" :validator="validator.decisions.$each[i].choices.$each[j].sliderMin" non-zero>Minimum Slider Value</number-input>
                <number-input label v-model="choice.sliderMax" :validator="validator.decisions.$each[i].choices.$each[j].sliderMax" non-zero>Maximum Slider Value</number-input>
                <text-input label v-model.trim="choice.sliderMinLabel">Slider Min Label</text-input>
                <text-input label v-model.trim="choice.sliderMidLabel">Slider Mid Label</text-input>
                <text-input label v-model.trim="choice.sliderMaxLabel">Slider Max Label</text-input>
                <checkbox-input v-model="choice.sliderLabelsBelow">Position Labels Below Slider</checkbox-input>
                <checkbox-input v-model="choice.sliderLabelTicks">Show Ticks On Slider For Each Label</checkbox-input>
                <checkbox-input v-model="choice.sliderHideValue">Hide Value</checkbox-input>
                <checkbox-input
                  v-if="!(choice.sliderHideValue)"
                  v-model="choice.sliderDisplayValueOnThumb"
                >Display Value On Slider Thumb</checkbox-input>
                <checkbox-input v-model="choice.sliderStartBlank">Slider Starts Blank Until Player Clicks (hides Choice Select Button until no sliders are blank)</checkbox-input>
                <template>
                  <div
                    v-if="choice.sliderColor && choice.sliderColor.length > 0"
                    :style="`border: 1px solid black; height: 15px; width: 15px; background-color: ${choice.sliderColor};`"
                  />

                  <text-input label v-model.trim="choice.sliderColor">Slider Color (supports all valid CSS colors, but rgba() recommended)</text-input>
                </template>

                <checkbox-input v-model="choice.sliderIsTime">Is Time Slider</checkbox-input>

                <checkbox-input v-model="choice.sliderDisplayIntervals">Use Interval</checkbox-input>
                <number-input v-if="choice.sliderDisplayIntervals" label v-model="choice.sliderInterval" :validator="validator.decisions.$each[i].choices.$each[j].sliderInterval" non-zero>Interval</number-input>
              </template>

              <select-input
                v-if="['matching', 'scale'].includes(decision.type)"
                label
                v-model="choice.correctCategory"
                :options="decision.categories || []"
                use="_id"
                :display="e => { return `${e.name}`; }"
              >Correct Category {{['scale'].includes(decision.type) ? '(optional)' : ''}}</select-input>

              <!-- TODO: metric adjustments for matrix type -->
              <array-input
                v-if="decision.type && !(['roleAllocation', 'matrix'].includes(decision.type))"
                :items="choice.metricAdjustments"
                :disabled="validator.decisions.$each[i].choices.$each[j].$invalid"
                border
              >
                <h5 slot="title">{{usingCorrect(decision, choice) ? 'Correct ' : ''}}Metric Adjustments</h5>

                <template slot="item" slot-scope="{ item: metricAdjustment, index: k }">
                  <div class="form-row">
                    <select-input class="col-sm-6" v-model="metricAdjustment.metric" :validator="validator.decisions.$each[i].choices.$each[j].metricAdjustments.$each[k].metric" :options="unused(metrics).by('_id').in(choice.metricAdjustments).by('metric').at(k)" use="_id" :display="m => { return `${m.name} (${m.type})`; }" required autofocus :remove="() => { choice.metricAdjustments.splice(k, 1); }" :to="(id) => { return `/blade/metrics/${id}/edit`; }">Metric</select-input>

                    <number-input class="col-sm-3" v-if="decision.type && decision.type !== 'roleAllocation' && decision.type !== 'projectAllocation'" v-model="metricAdjustment.adjustment">Adjustment</number-input>

                    <checkbox-input
                      class="col-sm-1"
                      v-if="decision.type && decision.type !== 'roleAllocation' && decision.type !== 'projectAllocation'"
                      v-model="metricAdjustment.setToValue"
                    >
                      Set
                    </checkbox-input>

                    <checkbox-input
                      v-if="decision.type && decision.type === 'sliders'"
                      class="col-sm-1"
                      v-model="metricAdjustment.multiplyAllocationValue"
                    >Slider Multiply</checkbox-input>

                    <checkbox-input
                      v-if="decision.type && decision.type === 'scale'"
                      class="col-sm-1"
                      v-model="metricAdjustment.multiplyAllocationValue"
                    >Category Value Multiply</checkbox-input>

                    <template class="form-row" v-if="decision.type === 'roleAllocation' || decision.type === 'projectAllocation'">
                      <number-input class="col-sm-3" v-model="metricAdjustment.allocationMinPercent" :validator="validator.decisions.$each[i].choices.$each[j].metricAdjustments.$each[k].allocationMinPercent" min-value max-value>Min %</number-input>

                      <number-input class="col-sm-3" v-model="metricAdjustment.allocationMaxPercent" :validator="validator.decisions.$each[i].choices.$each[j].metricAdjustments.$each[k].allocationMaxPercent" min-value max-value :custom-errors="[{ validator: 'isGreaterThan', message: 'Max % must be > Min %' }]">Max %</number-input>
                    </template>
                  </div>

                  <div class="form-row" v-if="decision.type === 'roleAllocation' || decision.type === 'projectAllocation'">
                    <checkbox-input class="col-sm-4" v-model="metricAdjustment.multiplyAllocationFraction">Multiply by Allocation Fraction</checkbox-input>

                    <checkbox-input class="col-sm-4" v-model="metricAdjustment.multiplyAllocationValue">Multiply by Allocation Value</checkbox-input>

                    <number-input class="col-sm-4" v-model="metricAdjustment.multiplier" :validator="validator.decisions.$each[i].choices.$each[j].metricAdjustments.$each[k].multiplier" non-zero>Multiplier</number-input>
                  </div>
                </template>
                <span slot="button">Add {{usingCorrect(decision, choice) ? 'Correct ' : ''}}Metric Adjustment</span>
              </array-input>

              <array-input
                v-if="usingCorrect(decision, choice) || (decision.type && ['multiselect', 'matching'].includes(decision.type))"
                :items="choice.unselectedMetricAdjustments"
                :disabled="validator.decisions.$each[i].choices.$each[j].$invalid"
                border
              >
                <h5 slot="title">{{usingCorrect(decision, choice) ? 'Incorrect' : 'Unselected'}} Metric Adjustments</h5>

                <template slot="item" slot-scope="{ item: unselectedMetricAdjustment, index: k }">
                  <div class="form-row">
                    <select-input
                      class="col-sm-6"
                      v-model="unselectedMetricAdjustment.metric"
                      :validator="validator.decisions.$each[i].choices.$each[j].unselectedMetricAdjustments.$each[k].metric"
                      :options="unused(metrics).by('_id').in(choice.unselectedMetricAdjustments).by('metric').at(k)"
                      use="_id"
                      :display="m => { return `${m.name} (${m.type})`; }"
                      required
                      autofocus
                      :remove="() => { choice.unselectedMetricAdjustments.splice(k, 1); }"
                      :to="(id) => { return `/blade/metrics/${id}/edit`; }"
                    >
                      Metric
                    </select-input>

                    <number-input class="col-sm-3" v-model="unselectedMetricAdjustment.adjustment">Adjustment</number-input>

                    <checkbox-input
                      class="col-sm-2"
                      v-model="unselectedMetricAdjustment.setToValue"
                    >
                      Set
                    </checkbox-input>
                  </div>
                </template>
                <span slot="button">Add {{usingCorrect(decision, choice) ? 'Incorrect' : 'Unselected'}} Metric Adjustment</span>
              </array-input>
            </collapsible-section>
            <collapsible-section
              v-if="decision.type && !['multiselect', 'matching', 'roleAllocation', 'projectAllocation', 'sliders', 'scale', 'matrix'].includes(decision.type)"
              :name="`Choice Narrative for: ${removeTags(choice.title) || '[missing title]'}`"
              :id="`subd-ch${i}-n${j}`"
              ellipsis
              bold
              indent
              :buttonStyle="{
                'font-size': '110%'
              }"
            >
              <narrative-input
                v-if="decision.type && !['multiselect', 'matching', 'roleAllocation', 'projectAllocation'].includes(decision.type)"
                :narratives="choice.narratives"
                :validator="validator.decisions.$each[i].choices.$each[j].narratives"
                :narrative-types="narrativeTypes"
                :speakers="speakers"
                :characters="characters"
                :variants="variants"
                required
                no-title
              >
                Choice Narrative
              </narrative-input>
            </collapsible-section>
            <collapsible-section
              v-if="decision.type && ['multiselect', 'matching'].includes(decision.type)"
              :name="`Choice ${(decision.type === 'matching') ? 'Correct' : 'Selected'} Narrative for: ${removeTags(choice.title) || '[missing title]'}`"
              :id="`subd-ch${i}-sn${j}`"
              ellipsis
              bold
              indent
              :buttonStyle="{
                'font-size': '110%'
              }"
            >
              <narrative-input
                v-if="decision.type && ['multiselect', 'matching'].includes(decision.type)"
                :narratives="choice.selectedNarratives"
                :validator="validator.decisions.$each[i].choices.$each[j].selectedNarratives"
                :narrative-types="narrativeTypes"
                :speakers="speakers"
                :characters="characters"
                :variants="variants"
                required
                no-title
              >
                Choice {{(decision.type === 'matching') ? 'Correct' : 'Selected'}} Narrative
              </narrative-input>
            </collapsible-section>
            <collapsible-section
              v-if="decision.type && ['multiselect', 'matching'].includes(decision.type)"
              :name="`Choice ${(decision.type === 'matching') ? 'Incorrect' : 'Unselected'} Narrative for: ${removeTags(choice.title) || '[missing title]'}`"
              :id="`subd-ch${i}-un${j}`"
              ellipsis
              bold
              indent
              :buttonStyle="{
                'font-size': '110%'
              }"
            >
              <narrative-input
                v-if="decision.type && ['multiselect', 'matching'].includes(decision.type)"
                :narratives="choice.unselectedNarratives"
                :validator="validator.decisions.$each[i].choices.$each[j].unselectedNarratives"
                :narrative-types="narrativeTypes"
                :speakers="speakers"
                :characters="characters"
                :variants="variants"
                required
                no-title
              >
                Choice {{(decision.type === 'matching') ? 'Incorrect' : 'Unselected'}} Narrative
              </narrative-input>
            </collapsible-section>

            <div style="background-color: grey; height: 2px; width: 100%;"></div>
          </template>
          <span slot="button">Add Choice</span>
        </array-input>
      </collapsible-section>

      <collapsible-section
        :name="`Role Decision Outros for: ${removeTags(decision.title) || '[missing title]'}`"
        :id="`subd-out${i}`"
        ellipsis
        bold
        indent
        :buttonStyle="{
          'font-size': '110%'
        }"
      >
        <array-input :items="decision.narratorOutros" :disabled="validator.decisions.$each[i].narratorOutros.$invalid && decision.narratorOutros.length > 0" :validator="validator.decisions.$each[i].narratorOutros" border>
          <h4 slot="title">Role Decision Narrator Outros</h4>

          <template slot="item" slot-scope="{ item: narratorOutro, index: j }">
            <text-input label v-model.trim="narratorOutro.text" :validator="validator.decisions.$each[i].narratorOutros.$each[j].text" required autofocus :remove="() => { decision.narratorOutros.splice(j, 1); }">Text {{ j + 1 }}</text-input>
          </template>
          <span slot="button">Add Narrator Outro</span>
        </array-input>

        <narrative-input
          :narratives="decision.outroNarratives"
          :validator="validator.decisions.$each[i].outroNarratives"
          :narrative-types="narrativeTypes"
          :speakers="speakers"
          :characters="characters"
          :variants="variants"
          required
        >
          Role Decision Outro Narrative
        </narrative-input>
      </collapsible-section>

      <div style="background-color: grey; height: 2px; width: 100%;"></div>
    </template>
    <span slot="button">Add Decision</span>
  </array-input>

  <collapsible-section
    name="Major Decision Outro Narrative"
    id="MDON"
    bold
    :buttonStyle="{
      'font-size': '140%'
    }"
  >
    <narrative-input
      :narratives="teamDecision.outroNarratives"
      :validator="validator.outroNarratives"
      :narrative-types="narrativeTypes"
      :speakers="speakers"
      :characters="characters"
      :variants="variants"
      required
      no-title
    >Major Decision Outro Narrative</narrative-input>
  </collapsible-section>

  <array-input :items="teamDecision.individualDecisions" :disabled="validator.individualDecisions.$invalid" :hide-button="teamDecision.individualDecisions.length >= individualDecisions.length">
    <h4 slot="title">Minor Decisions</h4>

    <select-input slot="item" slot-scope="{ item: individualDecision, index: i }" v-model="individualDecision._id" :validator="validator.individualDecisions.$each[i]._id" :options="unused(individualDecisions).by('_id').in(teamDecision.individualDecisions).by('_id').at(i)" use="_id" display="name" required autofocus :remove="() => { teamDecision.individualDecisions.splice(i, 1); }" :to="(id) => { return `/blade/minor-decisions/${id}/edit`; }">Minor Decision</select-input>
    <span slot="button">Add Minor Decision</span>
  </array-input>
</template>
</edit-page>
</template>

<script>
import { mapActions, mapMutations, mapGetters } from 'vuex';
import { required, requiredIf, minValue, maxValue } from 'vuelidate/lib/validators';

import { isGreaterThan, nonZero, narrative } from '../../../utils/validators';
import { unused } from '../../../utils/utils';

import EditPage from '../../ui/pages/EditPage';

import ArrayInput from '../../ui/inputs/ArrayInput';
import CheckboxInput from '../../ui/inputs/CheckboxInput';
import CollapsibleSection from '../../ui/inputs/CollapsibleSection';
import NarrativeInput from '../../ui/inputs/NarrativeInput';
import NumberInput from '../../ui/inputs/NumberInput';
import AssetSelectInput from '../../ui/inputs/AssetSelectInput';
import CharacterSelectInput from '../../ui/inputs/CharacterSelectInput';
import SelectInput from '../../ui/inputs/SelectInput';
import TextInput from '../../ui/inputs/TextInput';
import TooltipInput from '../../ui/inputs/TooltipInput';
import BranchPointRulesInput from '../../ui/inputs/BranchPointRulesInput';
import VoiceLineInput from '../../ui/inputs/VoiceLineInput';
import DecisionFreeDrag from '../../game/ui/DecisionFreeDrag';

export default {
  components: {
    EditPage,
    ArrayInput,
    CheckboxInput,
    CollapsibleSection,
    NarrativeInput,
    NumberInput,
    AssetSelectInput,
    CharacterSelectInput,
    SelectInput,
    TextInput,
    TooltipInput,
    BranchPointRulesInput,
    VoiceLineInput,
    DecisionFreeDrag,
  },
  data() {
    return {
      utils: {
        unused
      },
      teamDecision: {
        _id: '',
        name: '',
        title: '',
        description: '',
        scoringObjective: '',
        keyPerformanceMeasure: '',
        prompt: '',
        submitButtonText: '',
        backButtonText: '',
        feedback: false,
        hideTeam: false,
        hideCashInDashboard: false,
        hideCashInResults: false,
        disableReasoningForm: false,
        preventReasoningInputOverride: false,
        reasoningMinimumLength: 0,
        reasoningMinimumLengthOverride: false,
        skipResults: false,
        displayOneLineChoices: false,
        dynamicHeightChoices: false,
        topBarText: '',
        hideDashInNarratives: false,
        hideDashInChoiceNarratives: false,
        hideDashInOutroNarratives: false,
        hideDashInDecision: false,
        backgroundOverride: '',
        playerVariant: '',
        bubbleDecision: false,
        bubbleLeft: false,
        bubbleThought: false,
        bubbleXOffset: '', // string so you can specify CSS unit
        bubbleYOffset: '',
        choiceXOffset: '', // String to specify CSS unit
        choiceYOffset: '',
        reasonXOffset: '', // string so you can specify CSS unit
        reasonYOffset: '',
        rightCharacter: '',
        rightVariant: '',
        extraRightCharacter: '',
        extraRightVariant: '',
        noAnimation: false,
        waitIfNoDecision: false,
        isCheckpoint: false,
        checkpointLabel: '',
        end: false,
        next: undefined,
        hiddenTeamMetrics: [],
        preMetricAdjustments: [],
        decisions: [],
        individualDecisions: [],
        introNarratives: [],
        useLoopbackIntro: false,
        loopbackIntroNarratives: [],
        outroNarratives: [],
        tooltips: [],
        branchPointRules: [],
      },
      validator: {
        name: { required },
        title: { required },
        reasoningMinimumLength: { maxValue: maxValue(250), minValue: minValue(0) },
        checkpointLabel: {
          required: requiredIf(({ isCheckpoint }) => isCheckpoint)
        },
        hiddenTeamMetrics: {
          $each: {
            _id: { required }
          }
        },
        preMetricAdjustments: {
          $each: {
            metric: { required },
            adjustment: { required }
          }
        },
        introNarratives: narrative(),
        loopbackIntroNarratives: narrative(),
        outroNarratives: narrative(),
        branchPointRules: {
          $each: {
            metric: { required },
            type: { required },
            additionalConditions: {
              $each: {
                type: { required },
              }
            },
            metricAdjustments: {
              $each: {
                metric: { required },
              }
            },
            narratives: narrative(),
          }
        },
        decisions: {
          required,
          $each: {
            title: { required },
            type: { required },
            role: {
              required: requiredIf(({ type }) => type && type !== 'vote')
            },
            narratorIntros: {
              $each: {
                text: {
                  required
                }
              }
            },
            narratorOutros: {
              $each: {
                text: {
                  required
                }
              }
            },
            introNarratives: narrative(),
            outroNarratives: narrative(),
            formEntries: {
              // refers to decision.type
              required: requiredIf(({ type }) => type && ['form'].includes(type)),
              $each: {
                type: { required },
                prompt: { required },
                choices: {
                  // refers to formEntry.type
                  required: requiredIf(({ type }) => type && ['selection'].includes(type)),
                  $each: {
                    title: { required },
                    metricAdjustments: {
                      $each: {
                        metric: { required },
                      }
                    },
                  },
                },
              }
            },
            categories: {
              required: requiredIf(({ type }) => type && (['matching'].includes(type))),
              $each: {
                name: { required },
              },
            },
            choices: {
              required: requiredIf(({ type }) => type && !(['freeResponse', 'form'].includes(type))),
              $each: {
                title: {
                  required: requiredIf(() => {
                    // TODO: this if is always true, so this is never required
                    if (this.teamDecision.decisions.length === 0) return false;
                    const [{ type }] = this.teamDecision.decisions.slice(-1);
                    return type !== 'roleAllocation';
                  })
                },
                allocateToRole: {
                  required: requiredIf(() => {
                    // TODO: this if is always true, so this is never required
                    if (this.teamDecision.decisions.length === 0) return false;
                    const [{ type }] = this.teamDecision.decisions.slice(-1);
                    return type === 'roleAllocation';
                  })
                },
                cashAdjustment: { nonZero },
                metricAdjustments: {
                  $each: {
                    metric: { required },
                    allocationMinPercent: {
                      minValue: minValue(0),
                      maxValue: maxValue(100)
                    },
                    allocationMaxPercent: {
                      minValue: minValue(0),
                      maxValue: maxValue(100),
                      isGreaterThan: isGreaterThan('allocationMinPercent', 'allocationMaxPercent')
                    },
                    multiplier: { nonZero }
                  }
                },
                unselectedMetricAdjustments: {
                  $each: {
                    metric: { required }
                  }
                },
                selectedNarratives: narrative(),
                unselectedNarratives: narrative(),
                narratives: narrative()
              }
            }
          }
        },
        individualDecisions: {
          $each: {
            _id: { required }
          }
        }
      },
      previewDecision: null,
      windowInnerWidth: 10,
      autogenChoiceCount: 0,
      formEntryStub: { choices: [], }
    };
  },
  computed: {
    ...mapGetters([
      'classTypeName',
    ]),
    previewScale() {
      // 1600 x 900 reference size, scale to 2/3 window width, no upscale
      return Math.min(1, (0.66 * this.windowInnerWidth) / 1600);
    },
    tooltipTargetTypes() {
      return [
        'speechBubble',
        'characterLeft',
        'characterExtraLeft',
        'characterRight',
        'characterExtraRight',
        'choices',
        'choiceCarouselRightArrow',
        'choiceCarouselLeftArrow',
        'choiceCarouselSelect',
        'reasonAndConfirmChoiceBubble',
        'videoOrImage',
      ];
    }
  },
  methods: {
    ...mapMutations([
      'setDisableForcedLandscape',
    ]),
    ...mapActions([
      'createTeamDecision',
      'getTeamDecision',
      'getTeamDecisionTypes',
      'updateTeamDecision',
      'deleteTeamDecision'
    ]),
    removeTags(text) {
      return text
        ? (text
          .replace(/<(br|center|strong|div|span)(>|\/>| [^>]*>)/g, '') // start/standalone tags
          .replace(/<\/(br|center|strong|div|span)>/g, '')) // end tags
        : '';
    },
    usingCorrect(decision, choice = null) {
      return decision
        && (decision.type === 'matching'
          || (decision.type === 'scale' && choice && choice.correctCategory));
    },
    onDecisionImageChange(decision) {
      this.$set(decision, 'video', null);
    },
    onDecisionVideoChange(decision) {
      this.$set(decision, 'image', null);
    },
    onTypeChange(decision) {
      if (['freeResponse', 'form'].includes(decision.type)) {
        this.$set(decision, 'choices', []);
        this.$set(decision, 'video', null);
      }
      if (!(['choice', 'multiselect'].includes(decision.type)) && decision && decision.choices) {
        decision.choices.forEach(c => this.$set(c, 'removeIfPreviouslyChosen', false));
      }
    },
    onFormEntryTypeChange(formEntry) {
      if (['employmentStartDate'].includes(formEntry.type)) {
        this.$set(formEntry, 'choices', []);
      }
    },
    remove(items, index) {
      items.splice(index, 1);
    },
    up(items, index) {
      if (index <= 0) return;
      const [item] = items.splice(index, 1);
      items.splice(index - 1, 0, item);
    },
    down(items, index) {
      if (index >= items.length - 1) return;
      const [item] = items.splice(index, 1);
      items.splice(index + 1, 0, item);
    },
    autogenerateChoices(decision) {
      for (let i = 0; i < this.autogenChoiceCount; ++i) {
        decision.choices.push({ title: `${decision.choices.length + 1}` });
      }
    },
    getRoleName(roles, _id) {
      const rf = roles.find(r => r._id === _id);
      if (rf) {
        return rf.name;
      }
      return '';
    },
    // categoryOptions(decision) {
    //   let options = [];
    //   if (decision && decision.type === 'matching'
    //     && decision.categories && decision.categories.length > 0) {
    //     options =
    //       decision.categories
    //         .map((category) => {
    //           if (!(category.name) || !(category._id)) {
    //             return null;
    //           }
    //           return {
    //             text: category.name,
    //             value: category._id,
    //           };
    //         })
    //         .filter(o => o && o.text);
    //   }
    //   return options;
    // },
    dependentEntryOptions(decision, originIndex) {
      let options = [];
      if (decision && decision.type === 'form'
        && decision.formEntries && decision.formEntries.length > originIndex) {
        options =
          decision.formEntries
            .map((formEntry, index) => {
              if (index >= originIndex || !(formEntry.choices) || !(formEntry.choices.length)) {
                return null;
              }
              return {
                text: formEntry.prompt,
                value: (index - originIndex),
              };
            })
            .filter(o => o && o.text);
      }
      return options;
    },
    dependentChoiceOptions(decision, originIndex, dependentEntryOffset) {
      let options = [];
      if (decision && decision.type === 'form'
        && decision.formEntries && decision.formEntries.length > originIndex) {
        const entryIndex = originIndex + dependentEntryOffset;
        if (decision.formEntries.length > entryIndex && entryIndex >= 0) {
          const entry = decision.formEntries[entryIndex];
          if (entry.choices && entry.choices.length) {
            options =
              entry.choices
                .map((choice, index) => ({
                  text: choice.title,
                  value: index,
                }))
                .filter(o => o && o.text);
          }
        }
      }
      return options;
    },
    filteredChoiceDisplayTypes(decision, choiceDisplayTypes) {
      return choiceDisplayTypes.filter((t) => {
        if (decision.type === 'matching') {
          return t !== 'carousel';
        } else if (decision.type === 'choice') {
          return t !== 'freeDrag';
        }
        return true;
      });
    },
    handleResize() {
      this.windowInnerWidth = window.innerWidth;
    },
    variantOptions(characters, variants, characterId) {
      if (characterId) {
        const c = characters.find(ch => ch._id === characterId);
        if (c) {
          if (c.imageVariants && c.imageVariants.length > 0) {
            return c.imageVariants.map(v => v.name);
          }
          return [];
        }
      }
      return variants;
    }
  },
  beforeMount() {
    this.setDisableForcedLandscape(true);
  },
  mounted() {
    window.addEventListener('orientationchange', this.handleResize);
    window.addEventListener('resize', this.handleResize);
    this.handleResize();
  },
  beforeDestroy() {
    window.removeEventListener('orientationchange', this.handleResize);
    window.removeEventListener('resize', this.handleResize);
    // make sure disable forced landscape can't get left on
    this.setDisableForcedLandscape(false);
  },
};
</script>

<style lang="scss" scoped>
// .decision-preview {
//   // TODO: delete
//   background-image: url('../../../assets/ui/BuffetFullBG.png');
// }
</style>



// WEBPACK FOOTER //
// src/components/blade/pages/TeamDecisionEdit.vue